import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BaggageType } from '@websky/graphql';
import { Baggage } from '../../../../../BaggageService/types';
import { Passenger } from '../../../../types';
import { SelectedUpgrade } from '../../../types';
import { useTheme } from '../../../../../theme';
import { getActualBaggage, getSegmentsInBaggageGroup, isCanModifyServices } from '../../../utils';
import { getDisabled } from '../../../store/selector';
import { setServices } from '../../../store/actions';
import PassengerBaggageRadio from '../PassengerBaggageRadio/PassengerBaggageRadio';
import PassengerBaggageCounter from '../PassengerBaggageCounter/PassengerBaggageCounter';
import BaggageCounter from '../../../../BaggageCounter';

interface Props {
	baggageServices: Baggage[];
	passenger: Passenger;
	segmentId: string;
	isUnavailable?: boolean;
}

const PassengerBaggageControl: React.FC<Props> = ({ baggageServices, passenger, segmentId, isUnavailable }) => {
	const { BaggagePassenger: theme } = useTheme('UpgradeBaggageDialog');

	const dispatch = useDispatch();

	const isDisabled = useSelector(getDisabled);

	const isAllSegments = !segmentId;

	const actualBaggage = useMemo<Baggage | null>(() => {
		return getActualBaggage(baggageServices, !isUnavailable ? segmentId : undefined, true);
	}, [baggageServices, segmentId, isUnavailable]);

	const segmentIds = useMemo(() => {
		if (!actualBaggage?.segmentIds) {
			return [];
		}

		if (isAllSegments) {
			return [...getSegmentsInBaggageGroup(baggageServices)];
		}

		return actualBaggage.segmentIds.find(ids => ids.includes(segmentId)) ?? [];
	}, [baggageServices, actualBaggage, segmentId, isAllSegments]);

	const isCanModify = useMemo(() => isCanModifyServices(baggageServices, passenger), [baggageServices, passenger]);

	const isIncreaseDisabled = isDisabled || isUnavailable || !isCanModify || !actualBaggage?.canBeAdded;

	const controlProps = {
		segmentId,
		segmentIds,
		passenger,
		baggages: baggageServices
	};

	const onChange = useCallback(
		(type: 'radio' | 'counter') => (items: SelectedUpgrade[]) => {
			dispatch(setServices({ items, type }));
		},
		[dispatch]
	);

	if (!actualBaggage) {
		return (
			<div
				className={theme.counter_wrapper}
				data-test-id="baggage-counter"
				data-passenger-id={passenger.id}
				data-segment-id={segmentId}
			>
				<BaggageCounter isDecreaseDisabled isIncreaseDisabled />
			</div>
		);
	}

	if (actualBaggage.type === BaggageType.BaggageExcess) {
		return <PassengerBaggageRadio {...controlProps} disabled={isIncreaseDisabled} onChange={onChange('radio')} />;
	}

	return (
		<PassengerBaggageCounter
			{...controlProps}
			isIncreaseDisabled={isIncreaseDisabled}
			onChange={onChange('counter')}
		/>
	);
};

export default PassengerBaggageControl;
